import React, { useState, useRef } from 'react';
import { X, Upload, Download, AlertCircle, CheckCircle, FileText } from 'lucide-react';
import Papa from 'papaparse';

const CommissionUploadModal = ({ isOpen, onClose, onUploadComplete }) => {
    const [step, setStep] = useState(1); // 1: file upload, 2: column mapping, 3: results
    const [file, setFile] = useState(null);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [uploadResults, setUploadResults] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    // Commission database fields that can be mapped - updated for combined names
    const dbFields = [
        { key: 'submission_date', label: 'Submission Date', required: false },
        { key: 'name', label: 'Agent Name', required: true },
        { key: 'email', label: 'Email', required: true },
        { key: 'phone', label: 'Phone', required: false },
        { key: 'reservation_number', label: 'Reservation Number', required: false },
        { key: 'guest_name', label: 'Guest Name', required: false },
        { key: 'supplier', label: 'Supplier', required: false },
        { key: 'booking_date', label: 'Booking Date', required: false },
        { key: 'travel_date', label: 'Travel Date', required: false },
        { key: 'gross_commission', label: 'Gross Commission', required: true },
        { key: 'agent_commission', label: 'Agent Commission', required: false },
        // Legacy separate name fields (still supported for backward compatibility)
        { key: 'first_name', label: 'First Name (Legacy)', required: false },
        { key: 'last_name', label: 'Last Name (Legacy)', required: false },
        { key: 'guest_first_name', label: 'Guest First Name (Legacy)', required: false },
        { key: 'guest_last_name', label: 'Guest Last Name (Legacy)', required: false },
        { key: 'notes', label: 'Notes', required: false }
    ];

    const resetModal = () => {
        setStep(1);
        setFile(null);
        setCsvHeaders([]);
        setColumnMapping({});
        setUploadResults(null);
        setIsUploading(false);
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    const handleFileSelect = (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;

        if (!selectedFile.name.endsWith('.csv')) {
            setError('Please select a CSV file');
            return;
        }

        setFile(selectedFile);
        setError('');

        // Parse CSV headers
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const firstLine = text.split('\n')[0];
            const headers = firstLine.split(',').map(h => h.trim().replace(/"/g, ''));
            setCsvHeaders(headers);
            setStep(2);
        };
        reader.readAsText(selectedFile);
    };

    const handleColumnMappingChange = (dbField, csvColumn) => {
        setColumnMapping(prev => ({
            ...prev,
            [dbField]: csvColumn
        }));
    };

    const validateMapping = () => {
        const requiredFields = dbFields.filter(field => field.required);
        const missingFields = requiredFields.filter(field => !columnMapping[field.key]);
        
        if (missingFields.length > 0) {
            setError(`Please map required fields: ${missingFields.map(f => f.label).join(', ')}`);
            return false;
        }
        
        setError('');
        return true;
    };

    const handleUpload = async () => {
        if (!validateMapping()) return;

        setIsUploading(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('csvFile', file);
            formData.append('columnMapping', JSON.stringify(columnMapping));

            const response = await fetch('/api/commissions/upload', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Upload failed');
            }

            setUploadResults(data);
            setStep(3);

            // Notify parent component of successful upload
            if (onUploadComplete) {
                onUploadComplete(data);
            }

        } catch (error) {
            console.error('Upload error:', error);
            setError(error.message || 'Failed to upload CSV file');
        } finally {
            setIsUploading(false);
        }
    };

    const downloadFailedRows = () => {
        if (!uploadResults?.failedImports?.length) return;

        const csvContent = [
            // Header row
            csvHeaders.join(','),
            // Failed rows with error column
            ...uploadResults.failedImports.map(failed => {
                const row = csvHeaders.map(header => failed.data[header] || '').join(',');
                return `${row},"${failed.error}"`;
            })
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'failed_commissions.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Upload Commission CSV
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Step 1: File Upload */}
                    {step === 1 && (
                        <div className="space-y-6">
                            <div className="text-center">
                                <FileText className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    Select CSV File
                                </h3>
                                <p className="text-gray-600 mb-6">
                                    Upload a CSV file containing commission data. The file will be processed and matched with agents by email address.
                                </p>
                            </div>

                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600 mb-4">
                                    Choose a CSV file or drag and drop
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                >
                                    Select File
                                </button>
                            </div>

                            {error && (
                                <div className="flex items-center space-x-2 text-red-600">
                                    <AlertCircle className="w-5 h-5" />
                                    <span>{error}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Column Mapping */}
                    {step === 2 && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    Map CSV Columns
                                </h3>
                                <p className="text-gray-600 mb-6">
                                    Map your CSV columns to the commission database fields. Required fields are marked with *.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {dbFields.map(field => (
                                    <div key={field.key} className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            {field.label} {field.required && <span className="text-red-500">*</span>}
                                        </label>
                                        <select
                                            value={columnMapping[field.key] || ''}
                                            onChange={(e) => handleColumnMappingChange(field.key, e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">-- Select CSV Column --</option>
                                            {csvHeaders.map(header => (
                                                <option key={header} value={header}>
                                                    {header}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>

                            {error && (
                                <div className="flex items-center space-x-2 text-red-600">
                                    <AlertCircle className="w-5 h-5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <div className="flex justify-between pt-4">
                                <button
                                    onClick={() => setStep(1)}
                                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleUpload}
                                    disabled={isUploading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {isUploading ? 'Uploading...' : 'Upload & Process'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Results */}
                    {step === 3 && uploadResults && (
                        <div className="space-y-6">
                            <div className="text-center">
                                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    Upload Complete
                                </h3>
                            </div>

                            {/* Summary */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-medium text-gray-900 mb-3">Import Summary</h4>
                                <div className="grid grid-cols-3 gap-4 text-center">
                                    <div>
                                        <div className="text-2xl font-bold text-blue-600">
                                            {uploadResults.summary.totalRows}
                                        </div>
                                        <div className="text-sm text-gray-600">Total Rows</div>
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-green-600">
                                            {uploadResults.summary.successful}
                                        </div>
                                        <div className="text-sm text-gray-600">Successful</div>
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-red-600">
                                            {uploadResults.summary.failed}
                                        </div>
                                        <div className="text-sm text-gray-600">Failed</div>
                                    </div>
                                </div>
                            </div>

                            {/* Failed imports */}
                            {uploadResults.failedImports?.length > 0 && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-medium text-gray-900">Failed Imports</h4>
                                        <button
                                            onClick={downloadFailedRows}
                                            className="flex items-center space-x-2 px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                                        >
                                            <Download className="w-4 h-4" />
                                            <span>Download Failed Rows</span>
                                        </button>
                                    </div>
                                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                                        {uploadResults.failedImports.map((failed, index) => (
                                            <div key={index} className="p-3 border-b border-gray-100 last:border-b-0">
                                                <div className="text-sm font-medium text-gray-900">
                                                    Row {failed.row}
                                                </div>
                                                <div className="text-sm text-red-600">
                                                    {failed.error}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-center pt-4">
                                <button
                                    onClick={handleClose}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CommissionUploadModal;
